import {DataStatus} from 'platform/components';

import {memo, useCallback, useMemo} from 'react';

import {FeaturePickerItem, FeaturesPicker, FeatureSubCategory} from '@dms/shared';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {useApiEnums} from '../../../../../../hooks/useApiEnums';
import {collapseSelectedFeatureValues} from '../../../../../../utils/collapseSelectedFeatureValues';
import {expandSelectedFeatureValues} from '../../../../../../utils/expandSelectedFeatureValues';
import {useFilters} from '../../../FiltersContext/FiltersContext';

export function FilterFeatures(props: TestIdProps) {
  const {getEnum, featureSubCategories, isLoading, isError} = useApiEnums();
  const {filters, onUpdateFilters} = useFilters();
  const selectedFeatures = useMemo(() => filters.features || [], [filters.features]);
  const featureCategories = useMemo(() => getEnum('feature_category'), [getEnum]);
  const features = useMemo(() => getEnum('feature'), [getEnum]);

  // useMemo for correctness: stable reference required by MemoizedFeaturesPicker.
  const selectedFeaturesForPicker = useMemo(
    () => expandSelectedFeatureValues(selectedFeatures, features),
    [features, selectedFeatures]
  );

  const handleFeatureSelect = useCallback(
    (selected: string[]) => {
      onUpdateFilters('features', collapseSelectedFeatureValues(selected, features));
    },
    [features, onUpdateFilters]
  );

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <MemoizedFeaturesPicker
        featureCategories={featureCategories}
        features={features}
        subCategories={featureSubCategories}
        selectedFeatures={selectedFeaturesForPicker}
        onFeatureSelect={handleFeatureSelect}
        data-testid={props['data-testid']}
      />
    </DataStatus>
  );
}

interface MemoizedFeaturesPickerProps extends TestIdProps {
  featureCategories: FeaturePickerItem[];
  features: FeaturePickerItem[];
  subCategories: FeatureSubCategory[] | undefined;
  selectedFeatures: string[] | Nullish;
  onFeatureSelect: (selected: string[]) => void;
}

const MemoizedFeaturesPicker = memo((props: MemoizedFeaturesPickerProps) => (
  <FeaturesPicker
    categories={props.featureCategories}
    features={props.features}
    subCategories={props.subCategories}
    selected={props.selectedFeatures}
    onFeatureSelect={props.onFeatureSelect}
    allowSubItemMultiselect
    data-testid={suffixTestId('features', props)}
  />
));
